/**
 * Spec 034: the pair that leads the task — on its card and in «···».
 *
 * `TaskLeadState`: who leads, whether their agent is working now (acted in the last
 * 15 minutes) or its state is unknown since its last action, and the open requests: who is
 * asked what, and whether the work stands. `TaskHandover`: the assignee offers the task to
 * another member with a summary; it changes hands only when they press «Принимаю задачу».
 */
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import { Me } from "@/api/resources";
import type { PersonRequestOut, TaskLeadOut, TaskOut } from "@/api/types";
import { Button } from "@/components/ui/Button";
import { TextArea } from "@/components/ui/Field";
import { useToast } from "@/components/ui/Toast";
import { Caption } from "@/kit";
import { agentState, requestLine } from "@/lib/lead";
import { usePeople } from "@/lib/people";

export function TaskLeadState({ lead }: { lead: TaskLeadOut | null | undefined }) {
  if (!lead) return null;
  const agent = agentState(lead);
  return (
    <div className="space-y-1 text-meta text-p-text-muted">
      <p>
        Ведёт <b className="font-bold text-p-text">{lead.name}</b>
        {agent ? <> · {agent}</> : " · агент по задаче ещё не действовал"}
      </p>
      {lead.open_requests.length > 0 && (
        <ul className="space-y-0.5" aria-label="Открытые запросы">
          {lead.open_requests.map((request) => (
            <li key={request.id} className={request.blocking ? "text-p-text" : undefined}>Ждёт: {requestLine(request)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function TaskHandover({ task, closed, onDone }: { task: TaskOut; closed: boolean; onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const me = useQuery({ queryKey: ["me"], queryFn: Me.get });
  const people = usePeople();
  const [open, setOpen] = useState(false);
  const [to, setTo] = useState("");
  const [summary, setSummary] = useState("");
  const key = useRef(crypto.randomUUID());
  const offer = useMutation({
    mutationFn: () => api<PersonRequestOut>(`/api/v1/tasks/${task.id}/handover`, {
      method: "POST",
      body: { to_user_id: to, summary_md: summary.trim(), version: task.version },
      idempotent: key.current,
    }),
    onSuccess: () => {
      key.current = crypto.randomUUID();
      setOpen(false); setTo(""); setSummary("");
      toast.success("Передача отправлена: задача сменит исполнителя, когда её примут");
      for (const k of [["task", task.id], ["chat"], ["overview"]]) void qc.invalidateQueries({ queryKey: k });
      onDone();
    },
    onError: (error: Error) => {
      key.current = crypto.randomUUID();
      toast.error(error.message || "Не удалось передать задачу");
    },
  });
  if (closed || !me.data || task.assignee_id !== me.data.id) return null;
  if (!open) return <Button variant="secondary" block onClick={() => setOpen(true)}>Передать задачу</Button>;
  return (
    <section className="space-y-2">
      <Caption>Передать задачу</Caption>
      <select value={to} onChange={(e) => setTo(e.target.value)} aria-label="Кому передать"
        className="h-11 w-full rounded-compact border border-p-border bg-p-surface px-3 text-[16px] text-p-text">
        <option value="">Кому…</option>
        {(people.data ?? []).filter((u) => u.id !== me.data?.id).map((u) => <option key={u.id} value={u.id}>{u.display_name}</option>)}
      </select>
      <TextArea label="Сводка: где работа, что осталось, где материалы" value={summary} rows={4} maxLength={4000}
        onChange={(e) => setSummary(e.target.value)} />
      <p className="text-meta text-p-text-muted">Задача останется твоей, пока адресат не нажмёт «Принимаю задачу».</p>
      <div className="grid grid-cols-2 gap-2">
        <Button variant="strong" disabled={!to || !summary.trim()} loading={offer.isPending} onClick={() => offer.mutate()}>Передать</Button>
        <Button variant="secondary" onClick={() => setOpen(false)}>Отмена</Button>
      </div>
    </section>
  );
}
